import { useEffect, useRef, useState } from 'react';
import { COIN, IC, Svg } from '../art/icons.js';
import { api } from '../lib/api.js';
import { amountText, dayLabel, groupByDay, rowIcon, timeText, type ActivityFilter, type ActivityRow } from '../lib/activity.js';
import { useNav } from '../lib/router.js';
import { useStore } from '../lib/store.js';
import { Scroll, Spinner, TopBar } from './ui.js';

/** Runs `fn` again when the app comes back to the front (a top-up finishing in the background, a tab regained), not on the first mount. */
export function useRefetchOnReturn(fn: () => void): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') ref.current(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);
}

/*
 * The activity feed (CLAUDE.md §4 "Card", Saif 21 Oct 2026): the rows as a banking app lists transactions. The Card page
 * shows the last few; /card/activity the lot, with All, Points and Credit tabs, grouped by day, loading more as you go.
 * A row opens its order; a top-up opens the credit history. Points still on the way are greyed until collection.
 */

const ICONS: Record<ReturnType<typeof rowIcon>, string> = { order: IC.bag, topup: IC.card, bonus: COIN(18), reward: IC.gift, refund: IC.back, other: IC.spark };

/**
 * The rows as a bank lists transactions: a round icon, the title, a grey one-line subtitle, the amount on the right.
 * On the Card tile the subtitle is the day (or "on the way"); in the full list, which is already grouped by day, the time.
 */
export function ActivityRows({ rows, withTime = false }: { rows: readonly ActivityRow[]; withTime?: boolean }) {
  const s = useStore();
  const { navigate } = useNav();
  const open = (r: ActivityRow) => {
    if (r.orderId) navigate(`/order/${r.orderId}`);
    else if (r.topupId) s.openSheet({ kind: 'credit' });
  };
  return (
    <div className="actrows">
      {rows.map((r) => {
        const icon = rowIcon(r.type);
        const sub = r.pending ? 'Added when you collect' : withTime ? [timeText(r.at), r.sub].filter(Boolean).join(' · ') : [dayLabel(r.at), r.sub].filter(Boolean).join(' · ');
        const inner = (
          <>
            <span className={`ic ${icon}`}><Svg html={ICONS[icon]} /></span>
            <span className="grow col" style={{ gap: 2, minWidth: 0 }}><b className="one">{r.title}</b><span className="s one">{sub}</span></span>
            <span className={`amt tnum ${r.pending ? 'pending' : (r.points ?? r.pence ?? 0) > 0 ? 'in' : 'out'}`}>{amountText(r)}</span>
          </>
        );
        return r.orderId || r.topupId
          ? <button type="button" className={`r ${r.pending ? 'pending' : ''}`} key={r.id} onClick={() => open(r)}>{inner}</button>
          : <div className={`r ${r.pending ? 'pending' : ''}`} key={r.id}>{inner}</div>;
      })}
    </div>
  );
}

const TABS: [ActivityFilter, string][] = [['all', 'All'], ['points', 'Points'], ['credit', 'Credit']];

/** /card/activity: every row, by day, with All / Points / Credit tabs. */
export function ActivityPage() {
  const s = useStore();
  const { back } = useNav();
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const u = s.me?.customer;
  const load = async (f: ActivityFilter, before: string | null) => {
    setBusy(true);
    setFailed(false);
    try {
      const r = await api<{ rows: ActivityRow[]; next: string | null }>(`/api/me/activity?filter=${f}${before ? `&before=${encodeURIComponent(before)}` : ''}`);
      setRows((cur) => (before && cur ? [...cur, ...r.rows] : r.rows));
      setNext(r.next);
    } catch {
      // Not "Nothing here yet": that would tell someone with points that they have none (pre-launch audit).
      if (!before) setFailed(true);
      else s.toast("Couldn't load more. Try again.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!u) return;
    setRows(null);
    void load(filter, null);
  }, [u?.id, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useRefetchOnReturn(() => { if (u) void load(filter, null); }); // a top-up finishing in the background, the app regained
  const groups = rows ? groupByDay(rows) : [];
  return (
    <Scroll padStyle={{ gap: 16, paddingTop: 16 }}>
      <TopBar title="Activity" onBack={back} />
      <div className="pills" role="tablist" aria-label="Show">{TABS.map(([k, l]) => <button type="button" role="tab" className="pill" key={k} aria-selected={filter === k} onClick={() => setFilter(k)}>{l}</button>)}</div>
      {failed && !rows?.length ? (
        <div className="card empty" role="alert"><b>Couldn’t load your activity</b><span className="small muted">Your points and credit are safe. Check your connection and try again.</span><button type="button" className="btn2" disabled={busy} onClick={() => load(filter, null)}>Try again</button></div>
      ) : rows === null ? <Spinner /> : !rows.length ? (
        <div className="card empty"><b>Nothing here yet</b><span className="small muted">{filter === 'credit' ? 'Top-ups, credit you spend and refunds will show here.' : 'Points you earn and use will show here, order by order.'}</span></div>
      ) : groups.map((g) => (
        <section className="card acttile" key={g.key}>
          <h2 className="kick">{g.label}</h2>
          <ActivityRows rows={g.rows} withTime />
        </section>
      ))}
      {next && <button type="button" className="btn2" disabled={busy} onClick={() => load(filter, next)}>{busy ? 'Loading…' : 'Show more'}</button>}
    </Scroll>
  );
}
